/* eslint-disable arrow-body-style */

import { CommonToast } from "@src/common/CommonToast";
import { InvocaPhoneNumber } from "@src/common/InvocaPhoneNumber";
import { ProviderPhoto } from "@src/common/ProviderPhoto";
import { ThemedIcon } from "@src/common/ThemedIcon";
import {
  APP_ROUTES,
  APPOINTMENT_TYPE_NAME,
  type AppointmentTypeName,
  CLINICIAN_TYPE,
  LAYOUT_CONSTANTS,
  PRESCRIBER_UNDER_18_DURATION_MINUTES,
} from "@src/constants";
import { MEDIA_QUERIES } from "@src/constants/media-queries";
import { type FormDetails, useFormContext } from "@src/context";
import { useApplicationContext } from "@src/context/ApplicationContext";
import { useEnvironment } from "@src/context/EnvironmentContext";
import { useFeatureFlags } from "@src/context/FeatureFlagContext";
import { useInteractionGuard } from "@src/context/InteractionGuardContext";
import { useExclusiveAction } from "@src/hooks/useExclusiveAction";
import { useNavigateWithUTM } from "@src/hooks/useNavigateWithUTM";
import type { Provider } from "@src/interfaces/provider.interface";
import type { ProviderPhoto as ProviderPhotoData } from "@src/interfaces/provider-photo.interface";
import { ScheduleAppointmentService } from "@src/services";
import ClientLogsService from "@src/services/client-logs.service";
import { getCachedProviderPhoto, getProviderPhotos } from "@src/services/provider-photos.service";
import type {
  ScheduleAppointmentRequestPayload,
  SchedulePollingProgressUpdate,
} from "@src/services/schedule-appointment.service";
import { navigateToContactUs } from "@src/utils/contact-us-navigation";
import { getOrdinalSuffix, toAppointmentIsoDateTime } from "@src/utils/date-utils";
import { getProviderPhotoSrc } from "@src/utils/provider-photo-utils";
import {
  parseInsurancePackageId,
  resolveInsurancePackageId,
  sanitizePayerId,
} from "@src/utils/schedule-appointment-utils";
import { isUnder18AgeRange } from "@src/utils/scheduling-metadata";
import { isTestRecordBlocked, TEST_RECORD_BLOCK_MESSAGE } from "@src/utils/test-record-utils";
import { useMediaQuery } from "@uhg-abyss/web/hooks/useMediaQuery";
import { styled } from "@uhg-abyss/web/tools/styled";
import { Card } from "@uhg-abyss/web/ui/Card";
import { Heading } from "@uhg-abyss/web/ui/Heading";
import { Text } from "@uhg-abyss/web/ui/Text";
import {
  Fragment,
  type SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { patientInfoFormIds } from "../patientInformationNavigation";
import {
  createInitialSchedulingProgressSteps,
  getCompletedSchedulingStepPercentage,
  SchedulingProgressModal,
} from "./SchedulingProgressModal";

const getSessionDisplay = (
  careFormat?: string,
  clinicianType?: string,
): { iconName: "conversation" | "desktop"; label: string } => {
  const sessionType =
    clinicianType === CLINICIAN_TYPE.PRESCRIBER ? "medication management" : "therapy";
  const isInPerson = careFormat === "in-person";

  let iconName: "conversation" | "desktop";
  if (isInPerson) {
    iconName = "conversation";
  } else {
    iconName = "desktop";
  }

  return {
    iconName,
    label: isInPerson ? `In-person ${sessionType} session` : `Virtual ${sessionType} session`,
  };
};

// ─── Helpers ──────────────────────────────────────────────────────
type ValidationError = { isValid: false; logField: string };
type ValidationSuccess = { isValid: true };
type ValidationResult = ValidationError | ValidationSuccess;

const DEFAULT_SCHEDULE_POLLING_INTERVAL_MS = 2000;
const TERMINAL_PROGRESS_LINGER_MS = 2000;

const getAppointmentTypeName = ({
  careFormat,
  durationMinutes,
  isPrescriber,
}: {
  careFormat?: string;
  durationMinutes: number;
  isPrescriber: boolean;
}): AppointmentTypeName => {
  const isTelehealth = careFormat === "telehealth" || careFormat === "virtual";

  if (!isPrescriber) {
    return isTelehealth
      ? APPOINTMENT_TYPE_NAME.NEW_THERAPIST_TH_60
      : APPOINTMENT_TYPE_NAME.NEW_THERAPIST_60;
  }

  if (durationMinutes === PRESCRIBER_UNDER_18_DURATION_MINUTES) {
    return isTelehealth
      ? APPOINTMENT_TYPE_NAME.NEW_PRESCRIBER_TH_90
      : APPOINTMENT_TYPE_NAME.NEW_PRESCRIBER_90;
  }

  return isTelehealth
    ? APPOINTMENT_TYPE_NAME.NEW_PRESCRIBER_TH_60
    : APPOINTMENT_TYPE_NAME.NEW_PRESCRIBER_60;
};

const validateSchedulingPrerequisites = (
  userUuid: string | undefined,
  address: unknown,
  appointmentDetails: unknown,
  userTimezone: unknown,
  departmentId: unknown,
): ValidationResult => {
  const addr = address as { context_id?: unknown } | null | undefined;
  if (!userUuid || !addr?.context_id) {
    return { isValid: false, logField: "Missing required scheduling data" };
  }

  if (departmentId === undefined || departmentId === null) {
    return { isValid: false, logField: "Missing required scheduling data" };
  }

  const appt = appointmentDetails as
    | {
        providerId?: unknown;
        appointmentIds?: unknown;
        apptDate?: unknown;
        apptTime?: unknown;
      }
    | undefined;

  if (
    !appt?.providerId ||
    !Array.isArray(appt?.appointmentIds) ||
    appt.appointmentIds.length === 0 ||
    !appt.apptDate ||
    !appt.apptTime ||
    !userTimezone
  ) {
    return { isValid: false, logField: "Missing required scheduling data" };
  }

  return { isValid: true };
};

const validatePatientInsuranceData = (
  memberDetails: unknown,
  dob: unknown,
  paymentMethod: unknown,
  selectedPolicy: unknown,
): ValidationResult => {
  const member = memberDetails as
    | {
        firstName?: string;
        lastName?: string;
      }
    | undefined;

  const firstName = member?.firstName?.trim();
  const lastName = member?.lastName?.trim();

  if (!firstName || !lastName || !dob) {
    return {
      isValid: false,
      logField: "Missing required patient or insurance data",
    };
  }

  const isSelfPay = paymentMethod === "self_pay";
  if (!isSelfPay && parseInsurancePackageId(selectedPolicy) === undefined) {
    return {
      isValid: false,
      logField: "Missing required patient or insurance data",
    };
  }

  return { isValid: true };
};

export const ConfirmApptDetailsCard = () => {
  const isMobile = useMediaQuery(MEDIA_QUERIES.mobileMax);
  const { setHandleSubmit } = useFormContext(); // Access Form contex
  const navigate = useNavigateWithUTM(); // Add navigation hook
  const { runExclusive } = useExclusiveAction("patient-information:schedule-appointment");
  const navTimeoutRef = useRef<ReturnType<typeof globalThis.setTimeout> | null>(null); // Ref to store timeout ID
  const lingerTimeoutRef = useRef<ReturnType<typeof globalThis.setTimeout> | null>(null);
  const pollingAbortControllerRef = useRef<AbortController | null>(null);
  const acceptPollingProgressUpdatesRef = useRef(false);
  const isSchedulingProgressOpenRef = useRef(false);
  const [isSchedulingProgressOpen, setIsSchedulingProgressOpen] = useState(false);
  const [schedulingProgressSteps, setSchedulingProgressSteps] = useState(() => {
    return createInitialSchedulingProgressSteps();
  });

  // Add ApplicationContext usage
  const { data, markRouteAsComplete } = useApplicationContext();
  const { setSuppressGlobalOverlay } = useInteractionGuard();
  const testRecordBlocked = isTestRecordBlocked(data);
  const { FEATURE_SCHEDULE_POLLING_STATUS } = useFeatureFlags();
  const isFeaturePollingStatusEnabled = FEATURE_SCHEDULE_POLLING_STATUS;
  const { SCHEDULE_POLLING_INTERVAL_MS } = useEnvironment();
  const providerDetails =
    typeof data.selectedProvider === "object" && data.selectedProvider !== null
      ? data.selectedProvider
      : ({} as Provider);
  const appointmentDetails = data.appointmentDetails;
  const [providerPhoto, setProviderPhoto] = useState<ProviderPhotoData | undefined>(
    providerDetails.providerPhoto,
  );
  const [providerPhotoIsLoading, setProviderPhotoIsLoading] = useState(false);
  const hasProviderPhoto = Boolean(getProviderPhotoSrc(providerPhoto));
  const isInPersonAppointment = appointmentDetails?.careFormat === "in-person";
  const isPrescriber = providerDetails.clinicianType === CLINICIAN_TYPE.PRESCRIBER;
  const sessionDisplay = getSessionDisplay(
    appointmentDetails?.careFormat,
    providerDetails.clinicianType,
  );
  const pollingIntervalMs = useMemo(() => {
    const parsedInterval = Number(SCHEDULE_POLLING_INTERVAL_MS);
    if (!Number.isFinite(parsedInterval) || parsedInterval <= 0) {
      return DEFAULT_SCHEDULE_POLLING_INTERVAL_MS;
    }

    return Math.floor(parsedInterval);
  }, [SCHEDULE_POLLING_INTERVAL_MS]);
  const schedulingProgressPercentage = useMemo(() => {
    return getCompletedSchedulingStepPercentage(schedulingProgressSteps);
  }, [schedulingProgressSteps]);

  const closeSchedulingProgress = useCallback(() => {
    if (lingerTimeoutRef.current !== null) {
      clearTimeout(lingerTimeoutRef.current);
      lingerTimeoutRef.current = null;
    }
    acceptPollingProgressUpdatesRef.current = false;
    isSchedulingProgressOpenRef.current = false;
    setIsSchedulingProgressOpen(false);
  }, []);

  const lingerSchedulingProgress = useCallback(async () => {
    if (!isSchedulingProgressOpenRef.current) {
      return;
    }

    await new Promise<void>((resolve) => {
      lingerTimeoutRef.current = globalThis.setTimeout(() => {
        lingerTimeoutRef.current = null;
        resolve();
      }, TERMINAL_PROGRESS_LINGER_MS);
    });

    if (!isSchedulingProgressOpenRef.current) {
      return;
    }

    closeSchedulingProgress();
  }, [closeSchedulingProgress]);

  // Helper function to format the appointment date and time
  const formatAppointmentDateTime = () => {
    if (!appointmentDetails?.apptDate || !appointmentDetails?.apptTime) {
      return { date: "Date not available", time: "Time not available" };
    }

    try {
      // Parse the date (format: YYYY-MM-DD)
      const date = new Date(appointmentDetails.apptDate);
      const dayName = date.toLocaleDateString("en-US", { weekday: "long" });
      const month = date.toLocaleDateString("en-US", { month: "long" });
      const day = date.getDate();
      const ordinalSuffix = getOrdinalSuffix(day);

      // Parse and format time with timezone
      const time = appointmentDetails.apptTime;
      const timezone = appointmentDetails.userTimezone;

      return {
        date: `${dayName}, ${month} ${day}${ordinalSuffix}`,
        time: `${time} (${timezone})`,
      };
    } catch {
      return { date: "Invalid date", time: "Invalid time" };
    }
  };

  const FormSpacing = useMemo(
    () =>
      styled("div", {
        display: "flex",
        flexDirection: "column",
        gap: "$web.semantic.spacing.scale.sm",
      }),
    [],
  );

  const TherapistInfo = useMemo(
    () =>
      styled("div", {
        lineHeight: 0,
        display: "flex",
        alignItems: "center",
        gap: "1rem",
        paddingBottom: "24px",

        ".therapist-text": {
          display: "flex",
          flexDirection: "column",
          gap: "4px",
        },
        ".therapist-name": {
          fontSize: "16px",
          fontWeight: 700,
          lineHeight: "1",
        },
        ".title, .clinic-name": {
          fontSize: "16px",
          fontWeight: 400,
        },
      }),
    [],
  );

  const AppointmentDetails = useMemo(
    () =>
      styled("div", {
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: "1rem",
        textAlign: "center",
        ".appointment-date, .appointment-place, .appointment-type": {
          display: "flex",
          alignItems: "center",
          gap: "0.5rem",
          justifyContent: "flex-start",
          ".appt-text": {
            fontSize: "16px",
          },
        },
      }),
    [],
  );

  const IconContainer = useMemo(
    () =>
      styled("div", {
        border: "1px solid $app.color.default.border",
        backgroundColor: "$app.color.brand.primary",
        borderRadius: "5px",
        padding: "18px",
        overflow: "hidden",
      }),
    [],
  );

  const TherapistTextWrapper = useMemo(
    () =>
      styled("div", {
        display: "flex",
        flexDirection: "column",
        gap: "4px",
      }),
    [],
  );

  const AppointmentDetailItemContainer = useMemo(
    () =>
      styled("div", {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "0.5rem",
      }),
    [],
  );

  const AppointmentDetailTextWrapper = useMemo(
    () =>
      styled("div", {
        display: "flex",
        flexDirection: "column",
        textAlign: "left",
      }),
    [],
  );

  const showScheduleErrorToast = useCallback(
    (actionName: string) => {
      CommonToast({
        title: "Something went wrong",
        message: (
          <Fragment>
            An unexpected error occurred. Try again, or contact us at{" "}
            <strong>
              <InvocaPhoneNumber />
            </strong>
            .
          </Fragment>
        ),
        toastType: "error",
        toastIcon: "error",
        withCloseButton: true,
        onClose: () => {
          ClientLogsService.logWebAction(
            data.userUuid || "unknown",
            actionName,
            "Confirm Appointment Details Card Common Toast has closed - Redirecting to Contact Us",
          );
          if (navTimeoutRef.current) {
            clearTimeout(navTimeoutRef.current);
          }
          navigateToContactUs(navigate, data.userUuid, undefined, false);
        },
      });
    },
    [data.userUuid, navigate],
  );

  // Helper: Extract optional patient fields (2 levels)
  const buildOptionalPatientFields = () => {
    const fields: Record<string, unknown> = {};
    if (data.memberDetails?.streetAddress2) {
      fields.address2 = data.memberDetails.streetAddress2;
    }
    if (data.memberDetails?.genderIdentity) {
      fields.gender_identity = data.memberDetails.genderIdentity;
    }
    if (data.memberDetails?.legalSex) {
      fields.legal_sex = data.memberDetails.legalSex.toUpperCase();
    }
    if (data.memberDetails?.preferredPronouns) {
      fields.preferred_pronouns = data.memberDetails.preferredPronouns;
    }
    if (data.memberDetails?.language) {
      fields.preferred_language = data.memberDetails.language;
    }
    return fields;
  };

  // Helper: Extract policy holder fields (2 levels)
  const buildPolicyHolderFields = () => {
    const fields: Record<string, unknown> = {};
    if (data.paymentMethod === "self_pay") {
      return fields;
    }
    if (data.isPrimaryPolicyHolder) {
      const firstName = data.memberDetails?.firstName?.trim();
      const lastName = data.memberDetails?.lastName?.trim();
      fields.insurance_policy_holder_first_name = firstName;
      fields.insurance_policy_holder_last_name = lastName;
      fields.insurance_policy_holder_dob = data.dob;
      fields.insurance_policy_holder_sex = data.gender?.toUpperCase() ?? undefined;
      fields.relationship_to_insured = "self";
    } else {
      fields.insurance_policy_holder_first_name = data.policyholderFirstName ?? undefined;
      fields.insurance_policy_holder_last_name = data.policyholderLastName ?? undefined;
      fields.insurance_policy_holder_dob = data.policyholderDOB ?? undefined;
      fields.insurance_policy_holder_sex = data.policyholderGender?.toUpperCase() ?? undefined;
      fields.relationship_to_insured = data.relationshipToPolicyholder ?? undefined;
    }
    return fields;
  };

  // Helper: Extract prescriber-specific fields (2 levels)
  const buildPrescriberFields = () => {
    const fields: Record<string, unknown> = {};
    if (isPrescriber && data.patientScreenerAnswers) {
      const answers = data.patientScreenerAnswers as Record<string, unknown>;
      fields.question_id_1 = answers.hospitalized;
      fields.question_id_2 = answers.eatingDisorder;
      fields.question_id_3 = answers.seriousMentalIllness;
      fields.question_id_4 = answers.longActingInjectable;
      fields.question_id_5 = answers.opioidTreatment;
      fields.question_id_6 = answers.controlledSubstances;
      if (data.medMgmtInd) {
        fields.med_mgmt_ind = 1;
      }
    }
    return fields;
  };

  // Helper: Build request payload (3 levels max)
  const buildRequestBody = useCallback(() => {
    const address = data.address as {
      context_id?: number;
      position?: { lat?: number; lon?: number };
    };
    const apptDetails = data.appointmentDetails as
      | {
          providerId?: string;
          appointmentTypeId?: unknown;
          careFormat?: string;
          apptDate?: string;
          apptTime?: string;
          appointmentIds?: Array<string | number>;
        }
      | undefined;
    const selectedProvider = data.selectedProvider as
      | {
          departments?: Array<{ department_id?: unknown }>;
          sessionDuration?: number;
        }
      | undefined;

    const userTimezone = data.userTimezone?.standardName?.trim();
    const departmentId =
      (selectedProvider?.departments?.[0]?.department_id as number | undefined) ?? undefined;
    const parsedInsurancePackageId = parseInsurancePackageId(data.selectedPolicy);
    const payerId = sanitizePayerId(data.insurancePolicy?.payer_id);
    const firstName = data.memberDetails?.firstName?.trim() || "";
    const lastName = data.memberDetails?.lastName?.trim() || "";
    const isSelfPay = data.paymentMethod === "self_pay";
    const appointmentIds = (apptDetails?.appointmentIds as number[] | undefined) ?? undefined;
    const appointmentDateTime = toAppointmentIsoDateTime(
      apptDetails?.apptDate,
      apptDetails?.apptTime,
    );

    if (!appointmentDateTime) {
      return null;
    }

    const baseRequest = {
      user_uuid: data.userUuid as string,
      practice_id: address.context_id as number,
      provider_id: Number.parseInt(apptDetails?.providerId as string, 10),
      appointment_id: appointmentIds as number[],
      appointment_type_id: apptDetails?.appointmentTypeId,
      appointment_type_name: getAppointmentTypeName({
        careFormat: apptDetails?.careFormat,
        durationMinutes: selectedProvider?.sessionDuration || 0,
        isPrescriber,
      }),
      appointment_datetime: appointmentDateTime,
      insurance_package_id: resolveInsurancePackageId(isSelfPay, parsedInsurancePackageId),
      payer_id: isSelfPay ? undefined : payerId,
      member_id: data.memberId,
      patient_first_name: firstName,
      patient_last_name: lastName,
      patient_dob: data.dob ?? undefined,
      patient_email: data.memberDetails?.email,
      patient_phone_number: data.memberDetails?.phone,
      patient_phone_type: data.memberDetails?.phoneType,
      patient_preferred_contact: data.memberDetails?.preferredContactMethod,
      patient_voicemail_ok: data.memberDetails?.voicemailPreference?.toLowerCase() === "yes",
      department_id: departmentId,
      self_pay: isSelfPay,
      address1: data.memberDetails?.streetAddress1,
      state: data.memberDetails?.state_code,
      city: data.memberDetails?.city,
      zip: data.memberDetails?.zip,
      userLatitude: address.position?.lat,
      userLongitude: address.position?.lon,
      appointment_modality: apptDetails?.careFormat,
      ok_to_leave_vm: data.memberDetails?.voicemailPreference?.toLowerCase() === "yes",
      ...(data.userRole === "patient navigator"
        ? {
            ...(data.memberDetails?.voicemailConsent !== undefined
              ? { consenttocall: data.memberDetails.voicemailConsent.toLowerCase() === "yes" }
              : {}),
            ...(data.memberDetails?.smsConsent !== undefined
              ? { consenttotext: data.memberDetails.smsConsent === "subscribed" }
              : {}),
          }
        : {}),
      user_timezone: userTimezone,
      ...(data.ageRange?.trim()
        ? { signup_as_minor: isUnder18AgeRange(data.ageRange.trim()) ? 1 : 0 }
        : {}),
      ...buildOptionalPatientFields(),
      ...buildPolicyHolderFields(),
      ...buildPrescriberFields(),
      specialities: data.specialities ? data.specialities.join(",") : "",
      careformat: data.careFormat
        ? data.careFormat.charAt(0).toUpperCase() + data.careFormat.slice(1)
        : "",
    };

    return baseRequest;
  }, [data, isPrescriber]);

  const scheduleAppointmentWithPolling = useCallback(
    async (schedulePayload: ScheduleAppointmentRequestPayload): Promise<boolean> => {
      let isAsyncPollingFlow = false;
      pollingAbortControllerRef.current?.abort();
      const pollingController = new AbortController();
      pollingAbortControllerRef.current = pollingController;

      const pollingResult = await ScheduleAppointmentService.scheduleAppointmentWithPollingDomain({
        schedulePayload,
        userUuid: data.userUuid || "unknown",
        patientDOB: data.dob ?? "",
        pollingIntervalMs,
        signal: pollingController.signal,
        onAsyncAccepted: () => {
          isAsyncPollingFlow = true;
          acceptPollingProgressUpdatesRef.current = true;
          isSchedulingProgressOpenRef.current = true;
          setSchedulingProgressSteps(createInitialSchedulingProgressSteps());
          setIsSchedulingProgressOpen(true);
        },
        onProgressUpdate: (update: SchedulePollingProgressUpdate) => {
          if (!acceptPollingProgressUpdatesRef.current) {
            return;
          }

          setSchedulingProgressSteps(update.steps);
          if (update.isTerminal) {
            acceptPollingProgressUpdatesRef.current = false;
          }
        },
      });

      if (pollingAbortControllerRef.current === pollingController) {
        pollingAbortControllerRef.current = null;
      }

      if (pollingResult.kind === "success") {
        if (isAsyncPollingFlow && pollingResult.usedPolling) {
          await lingerSchedulingProgress();
        }

        if (!pollingResult.usedPolling) {
          ClientLogsService.logWebAction(
            data.userUuid || "unknown",
            "ConfirmApptDetailsCard.scheduleAppointmentWithPolling",
            `Schedule Appointment API returned ${pollingResult.statusCode ?? "success"} without a pollingId, treating as success`,
          );
        }
        return true;
      }

      if (pollingResult.kind === "schedule_failure") {
        closeSchedulingProgress();
        ClientLogsService.logError(
          data.userUuid || "unknown",
          "ConfirmApptDetailsCard.scheduleAppointmentWithPolling",
          `Appointment scheduling failed: ${pollingResult.error || pollingResult.message}`,
        );
        showScheduleErrorToast("ConfirmApptDetailsCard.CommonToast");
        return false;
      }

      if (pollingResult.kind === "completed_with_failure") {
        if (isAsyncPollingFlow) {
          await lingerSchedulingProgress();
        }
        ClientLogsService.logError(
          data.userUuid || "unknown",
          "ConfirmApptDetailsCard.scheduleAppointmentWithPolling",
          `Polling ended in a non-success terminal state: ${pollingResult.message}`,
        );
        showScheduleErrorToast("ConfirmApptDetailsCard.CommonToast");
        return false;
      }

      closeSchedulingProgress();
      ClientLogsService.logError(
        data.userUuid || "unknown",
        "ConfirmApptDetailsCard.scheduleAppointmentWithPolling",
        pollingResult.message,
      );
      showScheduleErrorToast("ConfirmApptDetailsCard.CommonToast");
      return false;
    },
    [
      closeSchedulingProgress,
      data.dob,
      data.userUuid,
      lingerSchedulingProgress,
      pollingIntervalMs,
      showScheduleErrorToast,
    ],
  );

  const scheduleAppointmentLegacy = useCallback(
    async (schedulePayload: ScheduleAppointmentRequestPayload): Promise<boolean> => {
      const response = await ScheduleAppointmentService.scheduleAppointment(schedulePayload);

      if (response.success) {
        return true;
      }

      showScheduleErrorToast("ConfirmApptDetailsCard.CommonToast");
      return false;
    },
    [data.userUuid, showScheduleErrorToast],
  );

  const scheduleAppointment = useCallback(async (): Promise<boolean> => {
    try {
      // Validate prerequisites
      const selectedProviderDepts = data.selectedProvider as {
        departments?: Array<{ department_id?: unknown }>;
      } | null;
      const departmentIdForValidation = selectedProviderDepts?.departments?.[0]?.department_id;
      const schedulingValidation = validateSchedulingPrerequisites(
        data.userUuid,
        data.address,
        data.appointmentDetails,
        data.userTimezone?.standardName?.trim(),
        departmentIdForValidation,
      );

      if (!schedulingValidation.isValid) {
        ClientLogsService.logError(
          data.userUuid || "unknown",
          "ConfirmApptDetailsCard.scheduleAppointment",
          schedulingValidation.logField,
        );
        showScheduleErrorToast("ConfirmApptDetailsCard.CommonToast - validation");
        return false;
      }

      // Validate patient/insurance data
      const patientValidation = validatePatientInsuranceData(
        data.memberDetails,
        data.dob,
        data.paymentMethod,
        data.selectedPolicy,
      );

      if (!patientValidation.isValid) {
        ClientLogsService.logError(
          data.userUuid || "unknown",
          "ConfirmApptDetailsCard.scheduleAppointment",
          patientValidation.logField,
        );
        showScheduleErrorToast("ConfirmApptDetailsCard.CommonToast - validation");
        return false;
      }

      // Build request body and call API
      const requestBody = buildRequestBody();
      if (!requestBody) {
        ClientLogsService.logError(
          data.userUuid || "unknown",
          "ConfirmApptDetailsCard.scheduleAppointment",
          "Invalid appointment datetime while building schedule request body",
          new Error("Unable to format appointment date/time for scheduling payload"),
        );
        showScheduleErrorToast("ConfirmApptDetailsCard.CommonToast - validation");
        return false;
      }

      const schedulePayload = requestBody as ScheduleAppointmentRequestPayload;

      return isFeaturePollingStatusEnabled
        ? await scheduleAppointmentWithPolling(schedulePayload)
        : await scheduleAppointmentLegacy(schedulePayload);
    } catch (error) {
      ClientLogsService.logError(
        data.userUuid || "unknown",
        "ConfirmApptDetailsCard.scheduleAppointment",
        "Error occurred in scheduleAppointment",
        error as Error,
      );
      showScheduleErrorToast("ConfirmApptDetailsCard.CommonToast - catch(error)");
      return false;
    }
  }, [
    isFeaturePollingStatusEnabled,
    data,
    showScheduleErrorToast,
    buildRequestBody,
    scheduleAppointmentWithPolling,
    scheduleAppointmentLegacy,
  ]);

  // Helper: Handle success navigation (2 levels of nesting)
  const handleScheduleSuccess = (): Promise<FormDetails> => {
    return new Promise((resolve) => {
      navTimeoutRef.current = globalThis.setTimeout(() => {
        navigate(APP_ROUTES.APPOINTMENT_CONFIRMED);
        resolve({ isCompleted: true });
      }, 1500);
    });
  };

  // Helper: Handle failure navigation (2 levels of nesting)
  const handleScheduleFailure = (): Promise<FormDetails> => {
    return new Promise((resolve) => {
      navTimeoutRef.current = globalThis.setTimeout(() => {
        navigateToContactUs(navigate, data.userUuid, undefined, true);
        resolve({ isCompleted: false });
      }, 5000);
    });
  };

  const handleSubmit = useCallback(async (): Promise<FormDetails> => {
    if (testRecordBlocked) {
      ClientLogsService.logWebAction(
        data.userUuid || "unknown",
        "ConfirmApptDetailsCard.handleSubmit",
        TEST_RECORD_BLOCK_MESSAGE,
      );
      CommonToast({
        title: "Scheduling unavailable",
        message: TEST_RECORD_BLOCK_MESSAGE,
        toastType: "error",
        toastIcon: "error",
        withCloseButton: true,
      });
      return { isCompleted: false };
    }

    const result = await runExclusive(async () => {
      const scheduled = await scheduleAppointment();
      if (scheduled) {
        markRouteAsComplete(APP_ROUTES.PATIENT_INFORMATION);
        return handleScheduleSuccess();
      }
      return handleScheduleFailure();
    });

    return result ?? { isCompleted: false };
  }, [
    runExclusive,
    scheduleAppointment,
    markRouteAsComplete,
    navigate,
    data.userUuid,
    testRecordBlocked,
  ]);

  const handleFormSubmit = (e: SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault(); // Prevent default form submission behavior
  };

  // Register handleSubmit in the context when the component mounts
  useEffect(() => {
    setHandleSubmit(() => handleSubmit);
  }, [handleSubmit, setHandleSubmit]); // Register handleSubmit onload

  useEffect(() => {
    isSchedulingProgressOpenRef.current = isSchedulingProgressOpen;
  }, [isSchedulingProgressOpen]);

  useEffect(() => {
    setSuppressGlobalOverlay(isSchedulingProgressOpen);
  }, [isSchedulingProgressOpen, setSuppressGlobalOverlay]);

  useEffect(() => {
    return () => {
      setSuppressGlobalOverlay(false);
    };
  }, [setSuppressGlobalOverlay]);

  useEffect(() => {
    return () => {
      acceptPollingProgressUpdatesRef.current = false;
      isSchedulingProgressOpenRef.current = false;
      pollingAbortControllerRef.current?.abort();
      pollingAbortControllerRef.current = null;
      if (lingerTimeoutRef.current !== null) {
        clearTimeout(lingerTimeoutRef.current);
        lingerTimeoutRef.current = null;
      }
      if (navTimeoutRef.current !== null) {
        clearTimeout(navTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    setProviderPhoto(providerDetails.providerPhoto);
    setProviderPhotoIsLoading(false);

    if (!providerDetails.providerId || !Number.isInteger(providerDetails.contextId)) {
      return;
    }

    const providerPhotoKey = {
      contextId: providerDetails.contextId,
      providerId: String(providerDetails.providerId),
    };
    const cachedProviderPhoto = getCachedProviderPhoto(providerPhotoKey);
    if (cachedProviderPhoto) {
      setProviderPhoto(cachedProviderPhoto);
      return;
    }

    let shouldUpdate = true;
    setProviderPhotoIsLoading(true);
    getProviderPhotos({
      providers: [providerPhotoKey],
      user_uuid: data.userUuid,
    })
      .then((response) => {
        if (!shouldUpdate) {
          return;
        }

        if (!response.success) {
          ClientLogsService.logError(
            data.userUuid || "unknown",
            "ConfirmApptDetailsCard.getProviderPhotos",
            `Failed to fetch provider photo: ${response.error}`,
          );
          return;
        }

        setProviderPhoto(response.data?.photos?.[0]);
      })
      .catch((error) => {
        ClientLogsService.logError(
          data.userUuid || "unknown",
          "ConfirmApptDetailsCard.getProviderPhotos",
          "Error fetching provider photo",
          error,
        );
      })
      .finally(() => {
        if (shouldUpdate) {
          setProviderPhotoIsLoading(false);
        }
      });

    return () => {
      shouldUpdate = false;
    };
  }, [
    providerDetails.providerId,
    providerDetails.contextId,
    providerDetails.providerPhoto,
    data.userUuid,
  ]);

  const { date: appointmentDate, time: appointmentTime } = formatAppointmentDateTime();

  return (
    <Fragment>
      <SchedulingProgressModal
        isOpen={isSchedulingProgressOpen}
        steps={schedulingProgressSteps}
        percentage={schedulingProgressPercentage}
      />
      <Card
        data-testid="test-id-confirm-appt-details-card"
        className={isMobile ? "v2card-form-mobile-style" : ""}
        css={{
          width: "100%",
          maxWidth: LAYOUT_CONSTANTS.maxFormWidth,
          padding: "2rem",
        }}
      >
        <form
          id={patientInfoFormIds.CONFIRM_APPT_DETAILS}
          data-testid="test-id-confirm-appt-details-form"
          onSubmit={handleFormSubmit}
        >
          <FormSpacing data-testid="test-id-confirm-appt-details-form-spacing">
            <TherapistInfo
              data-testid="test-id-confirm-appt-details-therapist-info"
              css={{
                flexDirection: isMobile ? "column" : "row",
                textAlign: isMobile ? "center" : "left",
              }}
            >
              <IconContainer
                data-testid="test-id-confirm-appt-details-provider-photo-container"
                css={{
                  padding: hasProviderPhoto ? "0" : "18px",
                  width: hasProviderPhoto ? "102px" : "auto",
                  height: hasProviderPhoto ? "102px" : "auto",
                }}
              >
                <ProviderPhoto
                  data-testid="test-id-confirm-appt-details-provider-photo"
                  providerPhoto={providerPhoto}
                  altText={`${providerDetails?.fullName ?? "Provider"} photo`}
                  fallbackIconSize={66}
                  fallbackIconStyle={{ width: "66px", height: "66px" }}
                  isLoading={providerPhotoIsLoading}
                  loadingLabel={`Loading ${providerDetails?.fullName ?? "provider"} photo`}
                />
              </IconContainer>
              <TherapistTextWrapper
                data-testid="test-id-confirm-appt-details-therapist-text"
                css={{ marginLeft: isMobile ? "0px" : "40px" }}
              >
                <Heading
                  className="therapist-name"
                  data-testid="test-id-confirm-appt-details-provider-name"
                >
                  {[providerDetails?.fullName, providerDetails?.providerType]
                    .filter(Boolean)
                    .join(", ")}
                </Heading>
                <Text
                  className="title"
                  data-testid="test-id-confirm-appt-details-provider-clinician-type"
                >
                  {providerDetails.clinicianType}
                </Text>
                <Text
                  className="clinic-name"
                  data-testid="test-id-confirm-appt-details-clinic-name"
                >
                  {appointmentDetails?.apptLocation?.clinic_name}
                </Text>
                {isInPersonAppointment && (
                  <Text
                    className="clinic-location"
                    data-testid="test-id-confirm-appt-details-clinic-location"
                  >
                    {`${appointmentDetails?.apptLocation?.city || ""}, ${appointmentDetails?.apptLocation?.state || ""}`}
                  </Text>
                )}
              </TherapistTextWrapper>
            </TherapistInfo>
            <AppointmentDetails
              data-testid="test-id-confirm-appt-details-appointment-details"
              css={{ marginLeft: isMobile ? "0px !important" : "72px" }}
            >
              <AppointmentDetailItemContainer
                className="appointment-date"
                data-testid="test-id-confirm-appt-details-appointment-date"
              >
                <ThemedIcon
                  data-testid="test-id-confirm-appt-details-appointment-calendar-themed-icon"
                  themeIconName="calendar"
                  themedImgProps={{ "aria-hidden": true }}
                  iconBrandProps={{ size: "40", icon: "calendar" }}
                />
                <AppointmentDetailTextWrapper
                  className="appt-text"
                  data-testid="test-id-confirm-appt-details-appointment-date-text"
                  css={{
                    marginLeft: isMobile ? "16px" : "40px",
                  }}
                >
                  <Text
                    data-testid="test-id-confirm-appt-details-appointment-date-text-date"
                    style={{ fontWeight: 700 }}
                  >
                    {appointmentDate}
                  </Text>
                  <Text data-testid="test-id-confirm-appt-details-appointment-date-text-time">
                    {appointmentTime}
                  </Text>
                </AppointmentDetailTextWrapper>
              </AppointmentDetailItemContainer>
              {isInPersonAppointment && appointmentDetails?.apptLocation && (
                <AppointmentDetailItemContainer
                  className="appointment-place"
                  data-testid="test-id-confirm-appt-details-appointment-place"
                >
                  <ThemedIcon
                    themeIconName="location_pin"
                    themedImgProps={{ "aria-hidden": true }}
                    iconBrandProps={{ size: "40", icon: "location_pin" }}
                  />
                  <Text
                    className="appt-text"
                    data-testid="test-id-confirm-appt-details-appointment-address-text"
                    css={{
                      textAlign: "left",
                      marginLeft: isMobile ? "16px" : "40px",
                    }}
                  >
                    {appointmentDetails.apptLocation.address_line_1}
                    {appointmentDetails.apptLocation.address_line_2 && (
                      <Fragment>
                        <br />
                        {appointmentDetails.apptLocation.address_line_2}
                      </Fragment>
                    )}
                    <br />
                    {appointmentDetails.apptLocation.city}, {appointmentDetails.apptLocation.state},{" "}
                    {appointmentDetails.apptLocation.zip_code}
                  </Text>
                </AppointmentDetailItemContainer>
              )}
              <AppointmentDetailItemContainer
                className="appointment-type"
                data-testid="test-id-confirm-appt-details-appointment-type"
              >
                <ThemedIcon
                  data-testid="test-id-confirm-appt-details-appointment-type-icon"
                  themeIconName={sessionDisplay.iconName}
                  themedImgProps={{ "aria-hidden": true }}
                  iconBrandProps={{ size: "40", icon: sessionDisplay.iconName }}
                />
                <AppointmentDetailTextWrapper
                  className="appt-text"
                  css={{
                    marginLeft: isMobile ? "16px" : "40px",
                  }}
                >
                  <Text
                    data-testid="test-id-confirm-appt-details-appointment-type-text-session-label"
                    style={{ fontWeight: 700 }}
                  >
                    {sessionDisplay.label}
                  </Text>
                  <Text data-testid="test-id-confirm-appt-details-appointment-type-text-session-duration">
                    {data.selectedProvider?.sessionDuration || 0} minutes
                  </Text>
                </AppointmentDetailTextWrapper>
              </AppointmentDetailItemContainer>
            </AppointmentDetails>
          </FormSpacing>
        </form>
      </Card>
    </Fragment>
  );
};
